import { useParams, Link } from "react-router-dom";
import { useState } from "react";
import { useJobs } from "@/hooks/useSiteData";
import useSeo from "@/hooks/useSeo";
import { ApplyForm } from "@/components/site/ApplyForm";
import { Link2, Check } from "lucide-react";

const IconWhatsApp = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm5.8 14.13c-.24.68-1.42 1.31-1.95 1.36-.5.05-1.13.24-3.71-.78-3.11-1.23-5.11-4.37-5.27-4.58-.15-.2-1.26-1.68-1.26-3.2 0-1.52.8-2.27 1.08-2.58.28-.31.61-.39.81-.39.2 0 .4 0 .58.01.19.01.44-.07.68.52.24.6.83 2.06.9 2.21.07.15.12.32.02.52-.1.2-.15.32-.29.5-.15.17-.31.39-.44.52-.15.15-.3.31-.13.6.17.29.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.35 1.46.29.15.46.12.63-.07.17-.2.72-.84.91-1.13.19-.29.39-.24.65-.15.27.1 1.7.8 1.99.95.29.15.48.22.55.34.07.12.07.7-.17 1.38Z"/>
  </svg>
);

const IconLinkedIn = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5ZM3 9h4v12H3V9Zm6 0h3.8v1.64h.05c.53-1 1.82-2.06 3.75-2.06C20.6 8.58 22 10.66 22 14.06V21h-4v-6.11c0-1.46-.03-3.33-2.03-3.33-2.03 0-2.34 1.58-2.34 3.22V21H9V9Z"/>
  </svg>
);

export default function JobDetails() {
  const { id } = useParams();
  const { data: jobs = [], isLoading } = useJobs();
  const job = jobs.find((j) => j.id === id);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useSeo(
    job ? `${job.title} — Lavora con noi` : "Posizione — Lavora con noi",
    job ? `${job.title}${job.location ? " · " + job.location : ""}${job.type ? " · " + job.type : ""}. Candidati ora in POWERCOM.` : "Dettagli posizione aperta in POWERCOM."
  );

  if (isLoading) {
    return (
      <section className="section">
        <div className="container"><p className="empty">Caricamento…</p></div>
      </section>
    );
  }

  if (!job) {
    return (
      <section className="section">
        <div className="container">
          <div className="empty" data-testid="job-not-found">
            <h2>Posizione non trovata.</h2>
            <p style={{ marginTop: 12 }}>L'annuncio potrebbe non essere più disponibile.</p>
            <Link className="btn" to="/lavora-con-noi" style={{ marginTop: 20 }}>Torna alle posizioni aperte</Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="page-hero">
        <div className="container reveal">
          <Link to="/lavora-con-noi" className="job-back" data-testid="job-back-link">← Posizioni aperte</Link>
          <span className="eyebrow light">Lavora con noi</span>
          <h1 data-testid="job-detail-title">{job.title}</h1>
          <div className="job-tags" style={{ marginTop: 16 }}>
            {job.location && <span className="job-tag light">📍 {job.location}</span>}
            {job.type && <span className="job-tag light">🕒 {job.type}</span>}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container contact-grid">
          <div className="job-detail-body reveal">
            <span className="eyebrow">Descrizione del ruolo</span>
            <div
              className="job-detail-desc"
              data-testid="job-detail-description"
              dangerouslySetInnerHTML={{ __html: job.description || "<p>Nessuna descrizione disponibile.</p>" }}
            />
            <div className="job-share" data-testid="job-share">
              <span className="job-share-label">Condividi questa offerta:</span>
              <div className="job-share-btns">
              <a
                className="job-share-btn wa"
                href={`https://wa.me/?text=${encodeURIComponent(`Offerta di lavoro in POWERCOM: ${job.title} — ${shareUrl}`)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-whatsapp"
              >
                <IconWhatsApp /> WhatsApp
              </a>
              <a
                className="job-share-btn li"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-linkedin"
              >
                <IconLinkedIn /> LinkedIn
              </a>
              <button
                type="button"
                className={`job-share-btn copy${copied ? " done" : ""}`}
                onClick={copyLink}
                data-testid="share-copy-link"
              >
                {copied ? <Check size={18} /> : <Link2 size={18} />} {copied ? "Link copiato!" : "Copia link"}
              </button>
              </div>
            </div>
          </div>
          <div className="form-card reveal" id="candidati">
            <span className="eyebrow">Candidati ora</span>
            <h2 style={{ fontSize: "1.4rem", margin: "6px 0 18px" }}>Invia la tua candidatura</h2>
            <ApplyForm position={job.title} requireCv={true} />
          </div>
        </div>
      </section>
    </>
  );
}
